// frontend/contexts/ChatContext.tsx

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { ChatSummary, Message, SenderType, SendMediaRequest, ChatSummaryMessageType, POLLING_INTERVAL } from '@openmirror/shared';
import { useActualApi, ApiError } from '../hooks/useActualApi';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';

interface ChatContextType {
  chats: ChatSummary[];
  selectedChat: ChatSummary | null;
  messages: Message[];
  isLoadingChats: boolean;
  isLoadingMessages: boolean;
  isSendingMessage: boolean;
  isTogglingBotPause: boolean;
  error: string | null;
  globalBotPaused: boolean;
  viewMode: 'active' | 'archived';
  hasMoreChats: boolean;
  isLoadingMoreChats: boolean;
  nextCursor: string | null;
  setViewMode: (mode: 'active' | 'archived') => void;
  selectChat: (chatId: string | null) => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
  sendMedia: (file: File, payload: SendMediaRequest) => Promise<void>;
  toggleBotPause: (chatId: string, pause: boolean) => Promise<void>;
  archiveChat: (chatId: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  restoreChat: (chatId: string) => Promise<void>;
  refreshChats: () => Promise<void>;
  loadMoreChats: () => Promise<void>;
  activate: () => void;
  deactivate: () => void;
}

const PREFETCH_CHAT_COUNT = 10;
const MESSAGE_CACHE_TTL_MS = 60_000; // 1 minute TTL to refresh stale threads

interface MessagesCacheEntry {
  messages: Message[];
  fetchedAt: number;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Module-level variable to allow deactivation from outside the hook
let moduleDeactivate: (() => void) | null = null;

// Exported function for DashboardLayout to call without using the hook
export const deactivateChatContext = () => {
  if (moduleDeactivate) {
    moduleDeactivate();
  }
};

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { idToken } = useAuth(); // Using only idToken; AuthContextType does not expose 'user'

  useEffect(() => {
    // Logs when the component mounts and ONLY when idToken string value actually changes
    console.log("ChatProvider: idToken updated or component mounted. Current idToken:", idToken ? "Present" : "Missing");
    if (!idToken) {
      console.warn("ChatProvider: No ID Token available. Chat functionality will be disabled until user authenticates.");
    }
  }, [idToken]);

  // If no idToken, provide a default context with disabled functionality
  if (!idToken) {
    console.warn("ChatProvider: No idToken available. Chat functionality will be disabled.");
    const defaultContextValue: ChatContextType = {
      chats: [],
      selectedChat: null,
      messages: [],
      isLoadingChats: false,
      isLoadingMessages: false,
      isSendingMessage: false,
      isTogglingBotPause: false,
      error: null,
      globalBotPaused: false,
      viewMode: 'active',
      hasMoreChats: false,
      isLoadingMoreChats: false,
      nextCursor: null,
      setViewMode: () => {},
      selectChat: async () => {},
      sendMessage: async () => {},
      sendMedia: async () => {},
      toggleBotPause: async () => {},
      archiveChat: async () => {},
      deleteChat: async () => {},
      restoreChat: async () => {},
      refreshChats: async () => {},
      loadMoreChats: async () => {},
      activate: () => {},
      deactivate: () => {}
    };
    
    return (
      <ChatContext.Provider value={defaultContextValue}>
        {children}
      </ChatContext.Provider>
    );
  }
  const api = useActualApi(); 
  const { socket, isConnected, isAuthenticated, joinChat, leaveChat, connectNow } = useWebSocket();

  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [selectedChat, setSelectedChat] = useState<ChatSummary | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [viewMode, setViewMode] = useState<'active' | 'archived'>('active');

  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isTogglingBotPause, setIsTogglingBotPause] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [globalBotPaused, setGlobalBotPaused] = useState(false);

  // Pagination state
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMoreChats, setHasMoreChats] = useState<boolean>(false);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState<boolean>(false);

  // Context activation state - controls whether data loading should happen
  const [isActive, setIsActive] = useState(false);

  const activate = useCallback(() => {
    setIsActive(true);
  }, []);

  const deactivate = useCallback(() => {
    setIsActive(false);
  }, []);

  // Register deactivate at module level for DashboardLayout to use
  useEffect(() => {
    moduleDeactivate = deactivate;
    return () => {
      moduleDeactivate = null;
    };
  }, [deactivate]);

  // To prevent calling setSelectedChat if component unmounted or selectedChat changed during async op
  const selectedChatRef = useRef(selectedChat); 
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  const messagesCacheRef = useRef<Map<string, MessagesCacheEntry>>(new Map());
  const prefetchSignatureRef = useRef<string>('');

  const messagesRequestAbortRef = useRef<AbortController | null>(null);

  const promoteChatToTop = (list: ChatSummary[], chatId: string): ChatSummary[] => {
    const index = list.findIndex(c => c.endUserWaId === chatId);
    if (index <= 0) {
      return list;
    }
    const updated = [...list];
    const [chat] = updated.splice(index, 1);
    updated.unshift(chat);
    return updated;
  };

  const executeWithRetry = async <T,>(operation: () => Promise<T>, retries = 3, baseDelayMs = 600): Promise<T> => {
    let attempt = 0;
    let lastError: any = null;

    while (attempt < retries) {
      try {
        return await operation();
      } catch (error: any) {
        lastError = error;

        const statusCode = error instanceof ApiError ? error.status : error?.status;
        if (statusCode && statusCode >= 400 && statusCode < 500 && statusCode !== 408 && statusCode !== 429) {
          throw error;
        }

        attempt += 1;
        if (attempt >= retries) {
          break;
        }

        const delay = baseDelayMs * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  };

  const hasRequestedSocketRef = useRef(false);
  const chatListPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isLoadingChatListRef = useRef(false);


  // --- CHAT LIST LOADING AND POLLING ---
  const loadChatListFromServer = useCallback(async (isBackgroundRefresh = false) => {
    if (!idToken) {
      setChats([]); // Clear chats if not authenticated
      if (!isBackgroundRefresh) setIsLoadingChats(false);
      return;
    }
    
    // Prevent concurrent requests to avoid browser resource exhaustion
    if (isLoadingChatListRef.current && isBackgroundRefresh) {
      console.log("ChatContext: Skipping background refresh - already loading");
      return;
    }
    
    isLoadingChatListRef.current = true;
    
    if (!isBackgroundRefresh) {
        setIsLoadingChats(true);
        console.log("ChatContext: loadChatListFromServer - Initial Load or Manual Refresh");
    } else {
        console.log("ChatContext: loadChatListFromServer - Background Poll");
    }
    setError(null);

    try {
      // Use the appropriate API based on viewMode
      const response = viewMode === 'archived' 
        ? await api.getArchivedChats() 
        : await api.getClientChats();
      
      // Debug logging for trigger notifications
      console.log('📊 Chats received from API:', {
        totalChats: response.chats?.length || 0,
        chatsWithTriggers: response.chats?.filter(c => c.hasTriggerNotification).length || 0,
        sampleChat: response.chats?.[0] ? {
          id: response.chats[0].endUserWaId,
          name: response.chats[0].userName,
          hasTriggerNotification: response.chats[0].hasTriggerNotification
        } : null
      });
      
      setChats(response.chats || []);
      setGlobalBotPaused(response.globalBotPaused || false);
      
      // Store pagination info
      setHasMoreChats(Boolean(response.hasMore));
      setNextCursor(response.nextCursor || null);
      
      // If a chat was selected and this is a background refresh, update its details
      // This helps keep the selectedChat object in sync with the list
      if (selectedChatRef.current && isBackgroundRefresh) {
        const updatedSelectedChatDetails = response.chats.find(c => c.endUserWaId === selectedChatRef.current?.endUserWaId);
        if (updatedSelectedChatDetails) {
            // Only update if the selected chat is still the same one we started with
            if(selectedChatRef.current?.endUserWaId === updatedSelectedChatDetails.endUserWaId) {
                setSelectedChat(prevSelected => ({...prevSelected, ...updatedSelectedChatDetails}));
            }
        } else {
            // If the selected chat is no longer in the list (e.g., deleted), deselect it
             if(selectedChatRef.current?.endUserWaId) { // Check if there was a selected chat
                setSelectedChat(null);
                setMessages([]); // Clear messages for the deselected chat
             }
        }
      }
    } catch (e: any) {
      setError(e.message || "Failed to load chats");
      console.error("loadChatListFromServer error:", e);
    } finally {
      if (!isBackgroundRefresh) setIsLoadingChats(false);
      isLoadingChatListRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, viewMode]); // Removed api - it creates new reference each render

  // Public refresh function for chat list
  const refreshChats = useCallback(async () => {
    console.log("ChatContext: Manual refreshChats called");
    await loadChatListFromServer(false); // Treat manual refresh like an initial load (shows loader)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Removed loadChatListFromServer - function is stable and safe to call

  // Load more chats (pagination)
  const loadMoreChats = useCallback(async () => {
    if (!idToken || !hasMoreChats || !nextCursor || isLoadingMoreChats) {
      console.log("ChatContext: loadMoreChats - Skipping (idToken, hasMore, cursor, or already loading check failed)");
      return;
    }
    
    setIsLoadingMoreChats(true);
    setError(null);
    console.log("ChatContext: loadMoreChats - Loading more chats with cursor:", nextCursor);
    
    try {
      const response = viewMode === 'archived'
        ? await api.getArchivedChats({ cursor: nextCursor, limit: 50 })
        : await api.getClientChats({ cursor: nextCursor, limit: 50 });
      
      // Append new chats and deduplicate by endUserWaId
      setChats(prev => {
        const merged = [...prev, ...(response.chats || [])];
        const seen = new Set<string>();
        return merged.filter(c => {
          if (seen.has(c.endUserWaId)) return false;
          seen.add(c.endUserWaId);
          return true;
        });
      });
      
      // Update pagination state
      setHasMoreChats(Boolean(response.hasMore));
      setNextCursor(response.nextCursor || null);
      
      console.log("ChatContext: loadMoreChats - Successfully loaded more chats, hasMore:", response.hasMore);
    } catch (e: any) {
      setError(e.message || "Failed to load more chats");
      console.error("loadMoreChats error:", e);
    } finally {
      setIsLoadingMoreChats(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, viewMode, hasMoreChats, nextCursor, isLoadingMoreChats]); // Removed api - it creates new reference each render

  // Effect for initial load of chat list
  useEffect(() => {
    let isActive = true;

    if (!idToken) {
      hasRequestedSocketRef.current = false;
      setChats([]);
      setSelectedChat(null);
      setMessages([]);
      setIsLoadingChats(false);
      messagesCacheRef.current.clear();
      prefetchSignatureRef.current = '';
      return () => {
        isActive = false;
      };
    }

    console.log("ChatContext: useEffect - Initial load trigger for chat list");
    loadChatListFromServer(false)
      .catch((error) => {
        console.error('ChatContext: Initial chat load failed', error);
      })
      .finally(() => {
        if (isActive && !hasRequestedSocketRef.current) {
          connectNow();
          hasRequestedSocketRef.current = true;
        }
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, connectNow]); // Removed loadChatListFromServer to prevent infinite loop

  // Fallback: Polling for chat list when WebSocket is not connected
  // This ensures the app still works even if WebSocket fails
  // Effect to reload chats when viewMode changes
  useEffect(() => {
    if (idToken) {
      console.log(`ChatContext: viewMode changed to ${viewMode}, reloading chats`);
      // Clear any existing polling interval before loading
      if (chatListPollingIntervalRef.current) {
        clearInterval(chatListPollingIntervalRef.current);
        chatListPollingIntervalRef.current = null;
      }
      loadChatListFromServer(false); // Reload with loading indicator when switching views
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, idToken]); // Only depend on viewMode and idToken, not loadChatListFromServer to avoid infinite loop

  // Effect for polling the chat list
  useEffect(() => {
    // Clear any existing polling interval first
    if (chatListPollingIntervalRef.current) {
      console.log('ChatContext: Clearing existing chat list polling interval before setting up new one');
      clearInterval(chatListPollingIntervalRef.current);
      chatListPollingIntervalRef.current = null;
    }

    // Only enable polling if WebSocket is not connected
    if (isConnected && isAuthenticated) {
      console.log('ChatContext: WebSocket is connected, skipping chat list polling');
      return;
    }

    if (idToken) {
      console.log(`ChatContext: WebSocket disconnected, falling back to chat list polling (${POLLING_INTERVAL}ms)`);
      chatListPollingIntervalRef.current = setInterval(() => {
        console.log("ChatContext: Polling (fallback) - calling loadChatListFromServer(true) for chat list");
        loadChatListFromServer(true); // isBackgroundRefresh = true
      }, POLLING_INTERVAL);
      
      return () => {
        if (chatListPollingIntervalRef.current) {
          console.log("ChatContext: Clearing chat list polling interval", chatListPollingIntervalRef.current);
          clearInterval(chatListPollingIntervalRef.current);
          chatListPollingIntervalRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, POLLING_INTERVAL, isConnected, isAuthenticated]); // Removed loadChatListFromServer to prevent infinite loop

  
  // --- MESSAGE LOADING AND POLLING FOR SELECTED CHAT ---
  const loadMessagesForChat = useCallback(async (chatId: string, options?: { forceRefresh?: boolean; silent?: boolean }) => {
    if (!idToken) return;

    const { forceRefresh = false, silent = false } = options ?? {};
    const cacheEntry = messagesCacheRef.current.get(chatId);
    const isCacheFresh = cacheEntry ? Date.now() - cacheEntry.fetchedAt < MESSAGE_CACHE_TTL_MS : false;

    if (!forceRefresh && cacheEntry && isCacheFresh) {
      if (selectedChatRef.current?.endUserWaId === chatId) {
        setMessages(cacheEntry.messages);
        setIsLoadingMessages(false);
      }
      return;
    }

    const abortController = new AbortController();
    const isSelectedChat = selectedChatRef.current?.endUserWaId === chatId;

    if (isSelectedChat) {
      messagesRequestAbortRef.current?.abort();
      messagesRequestAbortRef.current = abortController;
    }

    if (isSelectedChat && !silent) {
      setIsLoadingMessages(true);
    }

    if (isSelectedChat && !silent) {
      setError(null);
    }

    console.log(`ChatContext: loadMessagesForChat - Fetching messages for ${chatId}${silent ? ' (silent)' : ''}`);
    try {
      const response = viewMode === 'archived'
        ? await api.getArchivedChatMessages(chatId, { signal: abortController.signal })
        : await api.getChatMessages(chatId, { signal: abortController.signal });

      if (abortController.signal.aborted) {
        return;
      }

      const resolvedMessages = response.messages || [];
      messagesCacheRef.current.set(chatId, { messages: resolvedMessages, fetchedAt: Date.now() });

      if (selectedChatRef.current?.endUserWaId === chatId) {
        setMessages(resolvedMessages);
      }
    } catch (e: any) {
      if (e instanceof ApiError && e.data?.type === 'AbortError') {
        return;
      }

      if (selectedChatRef.current?.endUserWaId === chatId) {
        if (!silent) {
          setError(e.message || `Failed to load messages for ${chatId}`);
          setMessages([]);
        }
      }

      if (silent) {
        console.warn(`loadMessagesForChat (${chatId}) silent error:`, e);
      } else {
        console.error(`loadMessagesForChat (${chatId}) error:`, e);
      }
    } finally {
      if (selectedChatRef.current?.endUserWaId === chatId && messagesRequestAbortRef.current === abortController) {
        if (!silent) {
          setIsLoadingMessages(false);
        }
        messagesRequestAbortRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, viewMode]); // Removed api - it creates new reference each render

  useEffect(() => {
    return () => {
      messagesRequestAbortRef.current?.abort();
    };
  }, []);

  // Effect to load messages when a chat is selected (initial load for that chat)
  useEffect(() => {
    console.log('📨 ChatContext: Message load effect triggered', { 
      chatId: selectedChat?.endUserWaId, 
      hasIdToken: !!idToken 
    });
    
    if (!selectedChat?.endUserWaId || !idToken) {
      setMessages([]);
      setIsLoadingMessages(false);
      return;
    }

    const chatId = selectedChat.endUserWaId;
    const cacheEntry = messagesCacheRef.current.get(chatId);
    const isCacheFresh = cacheEntry ? Date.now() - cacheEntry.fetchedAt < MESSAGE_CACHE_TTL_MS : false;

    if (cacheEntry) {
      console.log(`📨 Loading ${cacheEntry.messages.length} messages from cache for ${chatId}`);
      setMessages(cacheEntry.messages);
      setIsLoadingMessages(false);
    } else {
      setMessages([]);
      setIsLoadingMessages(true);
    }

    if (!cacheEntry) {
      console.log(`ChatContext: useEffect - Loading messages for ${chatId} (no cache)`);
      loadMessagesForChat(chatId, { forceRefresh: true, silent: false });
    } else if (!isCacheFresh) {
      console.log(`ChatContext: useEffect - Refreshing stale cache for ${chatId}`);
      loadMessagesForChat(chatId, { forceRefresh: true, silent: true });
    }
  }, [selectedChat?.endUserWaId, idToken, loadMessagesForChat]); // Changed from selectedChat to selectedChat?.endUserWaId to avoid reloading on metadata updates

  // Prefetch the top chats so opening them feels instant
  useEffect(() => {
    if (!idToken || chats.length === 0) {
      return;
    }

    const topChats = chats.slice(0, PREFETCH_CHAT_COUNT);
    if (topChats.length === 0) {
      return;
    }

    const chatIds = topChats.map(chat => chat.endUserWaId);
    const signature = `${viewMode}:${chatIds.join('|')}`;
    const now = Date.now();
    const allFresh = chatIds.every(chatId => {
      const entry = messagesCacheRef.current.get(chatId);
      return Boolean(entry && now - entry.fetchedAt < MESSAGE_CACHE_TTL_MS);
    });

    if (signature === prefetchSignatureRef.current && allFresh) {
      return;
    }

    prefetchSignatureRef.current = signature;
    let cancelled = false;

    const prefetch = async () => {
      for (const chatId of chatIds) {
        if (cancelled) {
          break;
        }

        if (selectedChatRef.current?.endUserWaId === chatId && isLoadingMessages) {
          continue;
        }

        const entry = messagesCacheRef.current.get(chatId);
        const isFresh = entry ? Date.now() - entry.fetchedAt < MESSAGE_CACHE_TTL_MS : false;
        if (isFresh) {
          continue;
        }

        try {
          await loadMessagesForChat(chatId, { forceRefresh: true, silent: true });
        } catch (error) {
          if (!cancelled) {
            console.warn(`ChatContext: Prefetch failed for ${chatId}`, error);
          }
        }
      }
    };

    void prefetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats, idToken, isLoadingMessages, viewMode]); // Removed loadMessagesForChat to prevent infinite loop

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    console.log(`ChatContext: WebSocket state - isConnected: ${isConnected}, isAuthenticated: ${isAuthenticated}, hasSocket: ${!!socket}`);
    
    if (!socket || !isAuthenticated) {
      console.log('ChatContext: WebSocket not ready for event listeners');
      return;
    }

    console.log('ChatContext: Setting up WebSocket event listeners');

    // Listen for new messages
    const handleNewMessage = (data: { chatId: string; message: Message }) => {
      console.log('🔔 ChatContext: Received new_message via WebSocket:', {
        chatId: data.chatId,
        messageId: data.message.id,
        sender: data.message.sender,
        text: data.message.text?.substring(0, 50),
        type: data.message.type,
        currentlySelectedChat: selectedChatRef.current?.endUserWaId,
        isForSelectedChat: selectedChatRef.current?.endUserWaId === data.chatId
      });
      
      // Only add message if it's for the currently selected chat
      if (selectedChatRef.current?.endUserWaId === data.chatId) {
        console.log('ChatContext: Message is for selected chat, processing...');
        setMessages(prev => {
          // Avoid duplicates by checking if message ID already exists
          // OR if message has same text, sender, and timestamp (within 5 seconds)
          const isDuplicate = prev.some(m => {
            if (m.id === data.message.id) return true;
            
            // Check for recently sent operator messages with same content
            if (data.message.sender === 'operator' && m.sender === 'operator') {
              const timeDiff = Math.abs(new Date(m.timestamp).getTime() - new Date(data.message.timestamp).getTime());
              const sameContent = m.text === data.message.text && m.type === data.message.type;
              return sameContent && timeDiff < 5000; // Within 5 seconds
            }
            
            return false;
          });
          
          if (isDuplicate) {
            console.log('⚠️ ChatContext: Skipping duplicate WebSocket message:', data.message.id);
            return prev;
          }
          
          console.log(`✅ ChatContext: Adding ${data.message.sender} message to state (current: ${prev.length}, new total: ${prev.length + 1})`);
          const nextMessages = [...prev, data.message];
          messagesCacheRef.current.set(data.chatId, { messages: nextMessages, fetchedAt: Date.now() });
          console.log(`💾 ChatContext: Updated cache for ${data.chatId}, cache now has ${nextMessages.length} messages`);
          return nextMessages;
        });
      } else {
        console.log('📝 ChatContext: Message is NOT for selected chat, updating cache only');
        const cacheEntry = messagesCacheRef.current.get(data.chatId);
        if (cacheEntry && !cacheEntry.messages.some(m => m.id === data.message.id)) {
          messagesCacheRef.current.set(data.chatId, {
            messages: [...cacheEntry.messages, data.message],
            fetchedAt: Date.now()
          });
        }
      }

      // Update chat list with new lastMessage info
      setChats(prevChats => {
        const hasChat = prevChats.some(c => c.endUserWaId === data.chatId);
        if (!hasChat) {
          return prevChats;
        }
        const updated = prevChats.map(c => 
          c.endUserWaId === data.chatId
            ? {
                ...c,
                lastMessage: data.message.text || data.message.type || 'New message',
                lastMessageType: data.message.type as any,
                lastMessageSenderRole: data.message.sender,
                timestamp: data.message.timestamp,
                unreadCount: selectedChatRef.current?.endUserWaId === data.chatId ? 0 : (c.unreadCount || 0) + 1
              }
            : c
        );
        return promoteChatToTop(updated, data.chatId);
      });
    };

    // Listen for chat updates (bot pause status, etc.)
  const handleChatUpdate = (data: { endUserWaId: string; lastMessage?: string; lastMessageType?: string; timestamp?: string; lastMessageSenderRole?: string; updates?: Partial<ChatSummary> }) => {
      console.log('ChatContext: Received chat_update via WebSocket:', data);
      
      setChats(prevChats => {
        // Check if chat already exists
        const existingChat = prevChats.find(c => c.endUserWaId === data.endUserWaId);
        
        if (existingChat) {
          // Update existing chat
          const updates: Partial<ChatSummary> = data.updates || {};
          if (data.lastMessage !== undefined) updates.lastMessage = data.lastMessage;
          if (data.lastMessageType !== undefined) updates.lastMessageType = data.lastMessageType as ChatSummaryMessageType;
          if (data.timestamp !== undefined) updates.timestamp = data.timestamp;
          if (data.lastMessageSenderRole !== undefined) updates.lastMessageSenderRole = data.lastMessageSenderRole as ChatSummary['lastMessageSenderRole'];

          const updatedList = prevChats.map(c => 
            c.endUserWaId === data.endUserWaId ? { ...c, ...updates } : c
          );
          return promoteChatToTop(updatedList, data.endUserWaId);
        } else {
          // Add new chat to the list (for messages from new numbers)
          console.log('ChatContext: Adding new chat from WebSocket notification:', data.endUserWaId);
          
          // Format phone number for display (convert 27XXXXXXXXX to 0XXXXXXXXX)
          const userName = data.endUserWaId.startsWith('27') && data.endUserWaId.length > 2
            ? `0${data.endUserWaId.substring(2)}`
            : data.endUserWaId;
          
          const newChat: ChatSummary = {
            endUserWaId: data.endUserWaId,
            userName,
            userAvatar: '',
            lastMessage: data.lastMessage || 'New message',
            lastMessageType: (data.lastMessageType as ChatSummaryMessageType) || 'text',
            lastMessageSenderRole: (data.lastMessageSenderRole as ChatSummary['lastMessageSenderRole']) || 'unknown',
            timestamp: data.timestamp || new Date().toISOString(),
            unreadCount: 1,
            botPaused: false
          };
          
          return [newChat, ...prevChats];
        }
      });
      
      // Update selected chat if it's the one being updated
      if (selectedChatRef.current?.endUserWaId === data.endUserWaId && data.updates) {
        setSelectedChat(prev => prev ? { ...prev, ...data.updates } : prev);
      }
    };

    socket.on('new_message', handleNewMessage);
    socket.on('chat_update', handleChatUpdate);

    // Test: Log all new_message events to verify reception
    const testHandler = (data: any) => {
      console.log('🧪 TEST: Raw new_message event received:', data);
    };
    socket.on('new_message', testHandler);

    return () => {
      console.log('ChatContext: Cleaning up WebSocket event listeners');
      socket.off('new_message', handleNewMessage);
      socket.off('new_message', testHandler);
      socket.off('chat_update', handleChatUpdate);
    };
  }, [socket, isAuthenticated]);

  // Join/leave chat rooms via WebSocket when chat selection changes
  useEffect(() => {
    console.log(`🔌 ChatContext: WebSocket room join check - isConnected: ${isConnected}, isAuthenticated: ${isAuthenticated}, hasSocket: ${!!socket}, selectedChat: ${selectedChat?.endUserWaId || 'none'}`);
    
    if (!isAuthenticated) {
      console.log('⚠️ ChatContext: Not authenticated, cannot join room');
      return;
    }

    if (!isConnected) {
      console.log('⚠️ ChatContext: Not connected, cannot join room');
      return;
    }

    if (selectedChat?.endUserWaId) {
      console.log(`🚪 ChatContext: Attempting to join WebSocket room for chat: ${selectedChat.endUserWaId}`);
      joinChat(selectedChat.endUserWaId);
    }

    return () => {
      if (selectedChat?.endUserWaId) {
        console.log(`🚪 ChatContext: Leaving WebSocket room for chat: ${selectedChat.endUserWaId}`);
        leaveChat(selectedChat.endUserWaId);
      }
    };
  }, [selectedChat?.endUserWaId, isAuthenticated, isConnected, socket, joinChat, leaveChat]);

  // Fallback: Polling for messages when WebSocket is not connected
  // This ensures the app still works even if WebSocket fails
  useEffect(() => {
    // Only enable polling if WebSocket is not connected
    if (isConnected && isAuthenticated) {
      console.log('ChatContext: WebSocket is connected, skipping polling');
      return;
    }

    if (selectedChat?.endUserWaId && idToken) {
      console.log(`ChatContext: WebSocket disconnected, falling back to polling for ${selectedChat.endUserWaId} (${POLLING_INTERVAL}ms)`);
      const currentChatId = selectedChat.endUserWaId;
      const intervalId = setInterval(() => {
        if (selectedChatRef.current?.endUserWaId === currentChatId) {
          console.log(`ChatContext: Polling (fallback) - calling loadMessagesForChat(${currentChatId})`);
          loadMessagesForChat(currentChatId, { silent: true, forceRefresh: true });
        }
      }, POLLING_INTERVAL);
      
      return () => {
        console.log(`ChatContext: Clearing fallback polling interval for ${currentChatId}`);
        clearInterval(intervalId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat, idToken, POLLING_INTERVAL, isConnected, isAuthenticated]); // Removed loadMessagesForChat to prevent infinite loop


  // --- ACTION FUNCTIONS ---
  const selectChat = useCallback(async (chatId: string | null) => {
    // This function now primarily sets the selectedChat state.
    // The useEffect hook above will then trigger loading messages for it.
    if (!idToken) return;
    setError(null);

    if (!chatId) {
      setSelectedChat(null);
      setMessages([]);
      setIsLoadingMessages(false);
      if (messagesRequestAbortRef.current) {
        messagesRequestAbortRef.current.abort();
        messagesRequestAbortRef.current = null;
      }
      // messages will be cleared by the useEffect watching selectedChat
      return;
    }

    const chatSummary = chats.find(c => c.endUserWaId === chatId);
    if (chatSummary) {
      console.log(`ChatContext: selectChat - Selecting chat ${chatId}`);
      setSelectedChat(chatSummary);
      // Optionally mark as read immediately on frontend
      setChats(prevChats => prevChats.map(c => 
        c.endUserWaId === chatId ? { ...c, unreadCount: 0 } : c
      ));
    } else {
        setSelectedChat(null);
        // logging.warning is not defined here, use console.warn
        console.warn(`ChatContext: selectChat called with chatId ${chatId} not found in current chats list.`);
    }
  }, [idToken, chats]); // Removed api from here as message loading is handled by useEffect

  const sendMessage = useCallback(async (text: string) => {
    if (!idToken || !selectedChatRef.current?.endUserWaId) {
      setError("Cannot send message: No active chat or not authenticated.");
      return;
    }

    const trimmedText = text.trim();
    if (!trimmedText) {
      return;
    }

    const currentSelectedChatId = selectedChatRef.current.endUserWaId;
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const optimisticMessage: Message = {
      id: tempId,
      clientTempId: tempId,
      clientStatus: 'sending' as const,
      sender: SenderType.OPERATOR,
      text: trimmedText,
      type: 'text',
      timestamp: new Date().toISOString(),
      isRead: true,
    };

    setError(null);

    if (selectedChatRef.current?.endUserWaId === currentSelectedChatId) {
      setMessages(prev => {
        const next = [...prev, optimisticMessage];
        messagesCacheRef.current.set(currentSelectedChatId, { messages: next, fetchedAt: Date.now() });
        return next;
      });
    }

    try {
      const payload = { messageText: trimmedText };
      const sentMessageByOperator = await executeWithRetry(() => api.sendOperatorMessage(currentSelectedChatId, payload));

      if (selectedChatRef.current?.endUserWaId === currentSelectedChatId) {
        setMessages(prev => {
          const next = prev.map(msg => {
            if (msg.clientTempId === tempId) {
              return { ...sentMessageByOperator, clientStatus: 'sent' as const };
            }
            return msg;
          });
          messagesCacheRef.current.set(currentSelectedChatId, { messages: next, fetchedAt: Date.now() });
          return next;
        });
      }

      setChats(prevChats => {
        const hasChat = prevChats.some(c => c.endUserWaId === currentSelectedChatId);
        if (!hasChat) {
          return prevChats;
        }
        const updated = prevChats.map(c =>
          c.endUserWaId === currentSelectedChatId
            ? {
                ...c,
                lastMessage: trimmedText,
                lastMessageType: 'text' as ChatSummaryMessageType,
                lastMessageHasMedia: false,
                lastMessageSenderRole: SenderType.OPERATOR,
                timestamp: sentMessageByOperator.timestamp,
                unreadCount: 0,
              }
            : c
        );
        return promoteChatToTop(updated, currentSelectedChatId);
      });

      if (selectedChatRef.current?.endUserWaId === currentSelectedChatId) {
        setSelectedChat(prev => prev ? {
          ...prev,
          lastMessage: trimmedText,
          lastMessageType: 'text' as ChatSummaryMessageType,
          lastMessageHasMedia: false,
          lastMessageSenderRole: SenderType.OPERATOR,
          timestamp: sentMessageByOperator.timestamp,
          unreadCount: 0,
        } : prev);
      }
    } catch (e: any) {
      const message = e?.message || "Failed to send message";
      setError(message);
      console.error("sendMessage error:", e);

      if (selectedChatRef.current?.endUserWaId === currentSelectedChatId) {
        setMessages(prev => {
          const next = prev.map(msg => {
            if (msg.clientTempId === tempId) {
              return { ...msg, clientStatus: 'failed' as const, clientError: message };
            }
            return msg;
          });
          messagesCacheRef.current.set(currentSelectedChatId, { messages: next, fetchedAt: Date.now() });
          return next;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, executeWithRetry]); // Removed api - it creates new reference each render

  // Send media message (image, video, audio, document)
  const sendMedia = useCallback(async (file: File, payload: SendMediaRequest) => {
    if (!idToken || !selectedChatRef.current?.endUserWaId) {
      setError("Cannot send media: No active chat or not authenticated.");
      return;
    }
    const currentSelectedChatId = selectedChatRef.current.endUserWaId;

    setError(null);
    try {
      const sentMessageByOperator = await api.sendOperatorMedia(currentSelectedChatId, file, payload);

      if (selectedChatRef.current?.endUserWaId === currentSelectedChatId) {
        setMessages(prev => {
          const next = [...prev, sentMessageByOperator];
          messagesCacheRef.current.set(currentSelectedChatId, { messages: next, fetchedAt: Date.now() });
          return next;
        });
      }

      const caption = (sentMessageByOperator.media?.caption || sentMessageByOperator.text || '').trim();

      setChats(prevChats => {
        const hasChat = prevChats.some(c => c.endUserWaId === currentSelectedChatId);
        if (!hasChat) {
          return prevChats;
        }

        const updated = prevChats.map(c =>
          c.endUserWaId === currentSelectedChatId
            ? { 
                ...c, 
                lastMessage: caption,
                lastMessageType: payload.type,
                lastMessageHasMedia: true,
                lastMessageSenderRole: SenderType.OPERATOR,
                timestamp: sentMessageByOperator.timestamp, 
                unreadCount: 0 
              }
            : c
        );

        return promoteChatToTop(updated, currentSelectedChatId);
      });

      if (selectedChatRef.current?.endUserWaId === currentSelectedChatId) {
        setSelectedChat(prev => prev ? {
          ...prev,
          lastMessage: caption,
          lastMessageType: payload.type,
          lastMessageHasMedia: true,
          lastMessageSenderRole: SenderType.OPERATOR,
          timestamp: sentMessageByOperator.timestamp,
          unreadCount: 0,
        } : prev);
      }
    } catch (e: any) {
      setError(e.message || "Failed to send media");
      console.error("sendMedia error:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]); // Removed api - it creates new reference each render

  const toggleBotPause = useCallback(async (chatId: string, pause: boolean) => {
    if (!idToken) {
        setError("Not authenticated to perform this action.");
        return;
    }
    setIsTogglingBotPause(true);
    setError(null);

    // Optimistic UI Update
    const originalChats = [...chats];
    const originalSelectedChat = selectedChatRef.current ? {...selectedChatRef.current} : null;

    setChats(prev => prev.map(c => c.endUserWaId === chatId ? { ...c, botPaused: pause } : c));
    if (selectedChatRef.current?.endUserWaId === chatId) {
      setSelectedChat(prev => prev ? { ...prev, botPaused: pause } : null);
    }
    
    try {
      const payload = { pause };
      const response = await api.toggleBotPause(chatId, payload);
      
      if (!response.success || response.botPaused !== pause) {
          console.warn("Toggle bot pause server state mismatch or failure:", response);
          // Rollback if server state is definitive and different, or trigger a refresh.
          // For now, we assume optimistic update might be slightly ahead of server for a moment.
          // A refresh of chat list will bring server state.
          setChats(prev => prev.map(c => c.endUserWaId === chatId ? { ...c, botPaused: response.botPaused } : c));
           if (selectedChatRef.current?.endUserWaId === chatId) {
             setSelectedChat(prev => prev ? { ...prev, botPaused: response.botPaused } : null);
           }
      }

      // Add a local system message for immediate UI feedback - frontend dev confirmed this is FE responsibility
      const systemMessageText = pause ? "Chatbot has been paused by operator." : "Chatbot has been resumed by operator.";
      const systemMessage: Message = {
        id: `msg_sys_toggle_${Date.now()}`,
        sender: SenderType.OPERATOR, 
        text: systemMessageText,
        timestamp: new Date().toISOString(),
        isRead: true, // System messages are "read"
      };
      if(selectedChatRef.current?.endUserWaId === chatId){
      setMessages(prev => {
        const next = [...prev, systemMessage];
        messagesCacheRef.current.set(chatId, { messages: next, fetchedAt: Date.now() });
        return next;
      });
      }
      // No need to call performLoadChats(true) here if the optimistic update is good enough
      // and polling will eventually sync. Or if you want immediate server confirmation:
      // await loadChatListFromServer(true); 

    } catch (e: any) {
      setError(e.message || "Failed to toggle bot pause status");
      console.error("toggleBotPause error:", e);
      setChats(originalChats); // Rollback
      if (originalSelectedChat && selectedChatRef.current?.endUserWaId === originalSelectedChat.endUserWaId) {
         setSelectedChat(originalSelectedChat);
      }
    } finally {
        setIsTogglingBotPause(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, chats]); // `chats` is a dependency because we read from it for originalChats. Removed api - it creates new reference each render

  // Archive a conversation (moves it server-side to archivedConversations and removes from active list)
  const archiveChat = useCallback(async (chatId: string) => {
    if (!idToken) {
      setError("Not authenticated to perform this action.");
      return;
    }
    setError(null);
    try {
      await api.archiveChat(chatId);
      // Remove from active chats list
      setChats(prev => prev.filter(c => c.endUserWaId !== chatId));
      messagesCacheRef.current.delete(chatId);
      // Deselect and clear messages if this was the selected chat
      if (selectedChatRef.current?.endUserWaId === chatId) {
        setSelectedChat(null);
        setMessages([]);
        setIsLoadingMessages(false);
        if (messagesRequestAbortRef.current) {
          messagesRequestAbortRef.current.abort();
          messagesRequestAbortRef.current = null;
        }
      }
    } catch (e: any) {
      setError(e.message || "Failed to archive conversation");
      console.error("archiveChat error:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]); // Removed api - it creates new reference each render

  // Delete a conversation (permanent)
  const deleteChat = useCallback(async (chatId: string) => {
    if (!idToken) {
      setError("Not authenticated to perform this action.");
      return;
    }
    setError(null);
    try {
      await api.deleteChat(chatId);
      setChats(prev => prev.filter(c => c.endUserWaId !== chatId));
      messagesCacheRef.current.delete(chatId);
      if (selectedChatRef.current?.endUserWaId === chatId) {
        setSelectedChat(null);
        setMessages([]);
        setIsLoadingMessages(false);
        if (messagesRequestAbortRef.current) {
          messagesRequestAbortRef.current.abort();
          messagesRequestAbortRef.current = null;
        }
      }
    } catch (e: any) {
      setError(e.message || "Failed to delete conversation");
      console.error("deleteChat error:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]); // Removed api - it creates new reference each render

  // Restore an archived conversation
  const restoreChat = useCallback(async (chatId: string) => {
    if (!idToken) {
      setError("Not authenticated to perform this action.");
      return;
    }
    setError(null);
    try {
      await api.restoreChat(chatId);
      // Remove from archived chats list
      setChats(prev => prev.filter(c => c.endUserWaId !== chatId));
      messagesCacheRef.current.delete(chatId);
      // Deselect and clear messages if this was the selected chat
      if (selectedChatRef.current?.endUserWaId === chatId) {
        setSelectedChat(null);
        setMessages([]);
        setIsLoadingMessages(false);
        if (messagesRequestAbortRef.current) {
          messagesRequestAbortRef.current.abort();
          messagesRequestAbortRef.current = null;
        }
      }
      // Switch back to active view to see the restored chat
      setViewMode('active');
    } catch (e: any) {
      setError(e.message || "Failed to restore conversation");
      console.error("restoreChat error:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]); // Removed api - it creates new reference each render

  useEffect(() => {
    const hasPending = messages.some(msg => msg.clientStatus === 'sending');
    setIsSendingMessage(current => (current === hasPending ? current : hasPending));
  }, [messages]);

  return (
    <ChatContext.Provider value={{
      chats,
      selectedChat,
      messages,
      isLoadingChats,
      isLoadingMessages,
      isSendingMessage,
      isTogglingBotPause,
      error,
      globalBotPaused,
      viewMode,
      hasMoreChats,
      isLoadingMoreChats,
      nextCursor,
      setViewMode,
      selectChat,
      sendMessage,
      sendMedia,
      toggleBotPause,
      archiveChat,
      deleteChat,
      restoreChat,
      refreshChats,
      loadMoreChats,
      activate,
      deactivate
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};